// Customer detail propagation — copy a customer's CURRENT details onto every
// document that carries a snapshot of them.
//
// Every quote and invoice stores a copy of the customer's details from when it
// was saved, and shop drawings store the name. Documents are searched and
// sorted by those stored copies, so they are updated at WRITE time here rather
// than looked up live when displayed. The owner decided that this includes paid
// invoices and the name.
//
// ── WHAT IS COPIED ───────────────────────────────────────────────────────
//   quotes, invoices : customerName, customerNumber, customerPhone (primary),
//                      customerAddress
//   shopDrawings     : customerName
// (Documents carry no email field, so there is none to update.)
//
// ── WHAT IS DELIBERATELY NOT COPIED ──────────────────────────────────────
//   taxExempt, priceLevel, customerAccount — they feed the price, the tax or
//     the SALE/INVOICE label of an order that already happened. Changing them
//     on a paid invoice makes the editor's next save recompute tax and invent a
//     balance or a credit.
//   delivery address fields — a job site is not the customer's billing address.
//   line items, totals, payments and every other field about the order itself.
//
// Callers save the customer FIRST and then call this; it never throws, so the
// customer save can never depend on it. It is idempotent: it writes only the
// documents whose stored values differ, so a save with no relevant change
// writes nothing, and a document left behind by an earlier failure (or out of
// sync for any other reason) is corrected by the next save of that customer.

import { collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { getPrimaryPhone } from './customerPhone';

// Firestore's limit on writes in one batch.
const BATCH_LIMIT = 500;

// The fields to write on quotes and invoices, from a customer record as saved.
export function customerDocFields(customer) {
  return {
    customerName: customer?.name || '',
    customerNumber: customer?.customerNumber || '',
    customerPhone: getPrimaryPhone(customer),
    customerAddress: customer?.address || '',
  };
}

// The subset of `fields` whose stored value on `data` differs. A missing field
// counts as '' — a document with no customerPhone and a customer with no phone
// agree, and must not be rewritten.
export function changedFields(data, fields) {
  const out = {};
  Object.entries(fields).forEach(([k, v]) => {
    if ((data?.[k] ?? '') !== v) out[k] = v;
  });
  return out;
}

// One equality filter per collection — covered by the automatic single-field
// index, nothing to deploy.
async function docsFor(col, customerId) {
  const snap = await getDocs(query(collection(db, col), where('customerId', '==', customerId)));
  return snap.docs;
}

/**
 * Update every quote, invoice and shop drawing of `customerId` to match
 * `customer` (the customer record exactly as just saved).
 *
 * Never throws. Returns { total, updated, error }:
 *   total   — documents that needed updating
 *   updated — how many of those were written
 *   error   — null on success; otherwise the error that stopped it
 * When error is set, updated < total and the remaining documents were NOT
 * touched. Each batch is atomic, so no document is ever left with only some of
 * its fields changed.
 */
export async function propagateCustomerDetails(customerId, customer) {
  const result = { total: 0, updated: 0, error: null };
  if (!customerId) return result;
  try {
    const docFields = customerDocFields(customer);
    const drawingFields = { customerName: docFields.customerName };

    const [invoices, quotes, drawings] = await Promise.all([
      docsFor('invoices', customerId),
      docsFor('quotes', customerId),
      docsFor('shopDrawings', customerId),
    ]);

    const writes = [];
    [...invoices, ...quotes].forEach(d => {
      const diff = changedFields(d.data(), docFields);
      if (Object.keys(diff).length) writes.push({ ref: d.ref, diff });
    });
    drawings.forEach(d => {
      const diff = changedFields(d.data(), drawingFields);
      if (Object.keys(diff).length) writes.push({ ref: d.ref, diff });
    });
    result.total = writes.length;

    // Sequential, so on failure `updated` is exactly the batches that landed.
    for (let i = 0; i < writes.length; i += BATCH_LIMIT) {
      const chunk = writes.slice(i, i + BATCH_LIMIT);
      const batch = writeBatch(db);
      chunk.forEach(w => batch.update(w.ref, w.diff));
      await batch.commit();
      result.updated += chunk.length;
    }
  } catch (err) {
    console.error('Customer detail propagation failed:', err);
    result.error = err;
  }
  return result;
}

// What to tell the user about a propagation result. null when there is nothing
// worth saying (success). Always says the customer itself WAS saved.
export function propagationFailureMessage(result) {
  if (!result?.error) return null;
  const { total, updated } = result;
  const left = total - updated;
  if (total === 0) {
    // Failed while reading, before anything was written.
    return 'The customer was saved.\n\n' +
      'Their quotes, invoices and shop drawings could NOT be updated with the new details ' +
      '(the documents could not be read). No documents were changed.\n\n' +
      'Save the customer again to retry.';
  }
  return 'The customer was saved.\n\n' +
    `${updated} of ${total} document${total === 1 ? '' : 's'} were updated with the new details. ` +
    `The other ${left} were NOT updated and still show the old details.\n\n` +
    'Save the customer again to retry — only the documents still out of date will be changed.';
}
